import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Search, CloudUpload, Trash2, Zap, X } from 'lucide-react';
import '../pages/Clients.css';
import { apiFetch } from '../services/api';

const dataAtualISO = () => {
  const hoje = new Date();
  const doisDigitos = (n) => String(n).padStart(2, '0');
  return `${hoje.getFullYear()}-${doisDigitos(hoje.getMonth() + 1)}-${doisDigitos(hoje.getDate())}`;
};

const emptyCpflForm = {
  uc: '', instalacaoNova: '', pn: '', tipoPessoa: 'fisica', cpf: '',
  cnpj: '', inscricaoEstadual: '', dataAbertura: '',
  nomeResponsavel: '', cpfResponsavel: '', rgResponsavel: '',
  nome: '', nomeContato: '',
  rg: '', dataNascimento: '',
  nomeConjuge: '', cpfConjuge: '', rgConjuge: '',
  uf: '', cidade: '', bairro: '',
  logradouro: '', numero: '',
  cep: '', complemento: '',
  telResidencial: '', telCelular: '', telContato: '',
  email: '', dataCadastro: '',
  valorPlano: '',
  titularDoPlano: true,
};

const tipoOptions = [
  { value: 'contrato', label: 'Contrato' },
  { value: 'autorizacao', label: 'Autorização' },
  { value: 'outros', label: 'Outros' }
];

function CpflStep2({ onBack, onNext }) {
  const [form, setForm] = useState(() => ({ ...emptyCpflForm, dataCadastro: dataAtualISO() }));
  const [docs, setDocs] = useState([]); // { id, file, tipo, enviar }
  const [buscarLoading, setBuscarLoading] = useState(false);
  const fileRef = useRef();
  const [showInstalacoesModal, setShowInstalacoesModal] = useState(false);
  const [listaInstalacoes, setListaInstalacoes] = useState([]);
  const [cpfBuscado, setCpfBuscado] = useState('');

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const dataParaISO = (valor) => {
    const v = String(valor || '').trim();
    let dia, mes, ano;
    let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);

    if (m) {
      [, ano, mes, dia] = m;
    } else {
      m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
      if (m) {
        [, dia, mes, ano] = m;
      } else {
        const d = v.replace(/\D/g, '');
        if (d.length !== 8) return '';
        dia = d.slice(0, 2);
        mes = d.slice(2, 4);
        ano = d.slice(4, 8);
      }
    }

    const data = new Date(Number(ano), Number(mes) - 1, Number(dia));
    if (
      data.getFullYear() !== Number(ano) ||
      data.getMonth() !== Number(mes) - 1 ||
      data.getDate() !== Number(dia)
    ) return '';

    return `${ano}-${mes}-${dia}`;
  };

  const formatDateForInput = (dateStr) => dataParaISO(dateStr);

  // dadosBusca: os dados que já vieram da primeira busca (ConsultaInstalacoesDocumento,
  // que sempre funciona) — servem de reserva caso o ValidarConsulta falhe (a
  // CPFL tem um bug: instalações sem "Nome" cadastrado quebram esse endpoint
  // específico com "Object reference not set to an instance of an object.").
  const carregarDadosInstalacao = async (uc, pn, cpf, nome, dadosBusca = {}) => {
    setBuscarLoading(true);
    set('uc', uc);
    const novaUc = dadosBusca.NovaUC;
    if (novaUc !== undefined && novaUc !== null && String(novaUc).trim() !== '') {
      set('instalacaoNova', String(novaUc).trim());
    }
    set('pn', pn);
    set('cpf', cpf);
    set('nome', nome || '');
    set('cidade', dadosBusca.Cidade || '');
    set('bairro', dadosBusca.Bairro || '');
    set('logradouro', dadosBusca.Rua || dadosBusca.Endereco || '');
    set('numero', dadosBusca.NumImovel || '');
    set('cep', dadosBusca.CEP || '');
    try {
      const token = localStorage.getItem('token');
      const validarResponse = await fetch('/api/cpfl/validar-consulta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ instalacao: uc, pn: pn, pf_pj: 1, cpf: cpf, cnpj: '' })
      });

      if (validarResponse.status === 401) {
        alert('Sessão expirada. Faça login novamente para consultar a CPFL.');
        return false;
      }

      const validarData = await validarResponse.json();
      if (!validarData.sucesso) {
        console.warn('ValidarConsulta falhou, usando dados da busca inicial:', validarData.mensagem);
        alert('Não foi possível confirmar todos os dados na CPFL (o campo Nome pode estar sem cadastro no sistema deles). Preenchemos o que já tínhamos encontrado — confira e complete manualmente o que faltar.');
        return true;
      }
      const dados = validarData.dados;
      set('nome', dados.nome || nome || '');
      set('uf', dados.uf || '');
      set('cidade', dados.cidade || dadosBusca.Cidade || '');
      set('bairro', dados.bairro || dadosBusca.Bairro || '');
      set('logradouro', dados.logradouro || dadosBusca.Rua || dadosBusca.Endereco || '');
      set('numero', dados.numero || dadosBusca.NumImovel || '');
      set('complemento', dados.complemento || '');
      set('cep', dados.cep || dadosBusca.CEP || '');
      return true;
    } catch (err) {
      console.error(err);
      alert('Erro de conexão ao confirmar dados na CPFL — os dados já encontrados foram mantidos, confira o formulário.');
      return true;
    } finally {
      setBuscarLoading(false);
    }
  };

  const isoParaBR = (valor) => {
    const iso = dataParaISO(valor);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
  };
  const brParaISO = (valor) => dataParaISO(valor);
  const mascaraData = (v) => {
    const d = (v || '').replace(/\D/g, '').slice(0, 8);
    if (d.length >= 5) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
    if (d.length >= 3) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return d;
  };

  const buscarDadosCnpjWs = async (cnpj) => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/cpfl/consultar-cnpj/${cnpj}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.status === 401) {
        alert('Sessão expirada. Faça login novamente.');
        return false;
      }
      const data = await resp.json();
      if (!data.sucesso) {
        alert(`Não foi possível consultar os dados do CNPJ: ${data.mensagem || 'erro desconhecido'}\nVocê pode preencher manualmente.`);
        return false;
      }
      const d = data.dados || {};
      if (d.situacaoCadastral && d.situacaoCadastral.toLowerCase() !== 'ativa') {
        alert(`Atenção: a situação cadastral deste CNPJ é "${d.situacaoCadastral}".`);
      }
      // telefone: 9 dígitos após o DDD começando com 9 = celular; senão residencial
      const tel = d.telefone || '';
      const ehCelular = tel.length === 11 && tel[2] === '9';
      const novos = {
        nome: d.razaoSocial,
        inscricaoEstadual: d.inscricaoEstadual,
        dataAbertura: isoParaBR(d.dataAbertura),
        logradouro: d.logradouro,
        numero: d.numero,
        complemento: d.complemento,
        bairro: d.bairro,
        cidade: d.cidade,
        uf: d.uf,
        cep: d.cep,
        email: d.email,
        telCelular: ehCelular ? tel : '',
        telResidencial: !ehCelular ? tel : '',
      };
      const respInfo = d.responsavel || {};
      const cand = Array.isArray(d.candidatos) ? d.candidatos : [];
      novos.nomeResponsavel = respInfo.nome;
      if (cand.length === 1) {
        novos.cpfResponsavel = cand[0].cpf;
        novos.rgResponsavel = cand[0].rg;
      }
      // Só preenche campos que ainda estão vazios (não sobrescreve o que o usuário digitou)
      setForm((p) => {
        const next = { ...p };
        Object.entries(novos).forEach(([k, v]) => { if (v && !String(p[k] || '').trim()) next[k] = v; });
        return next;
      });
      if (respInfo.nome && cand.length !== 1) {
        alert(`Responsável identificado pela Receita: ${respInfo.nome}${respInfo.cpfMascarado ? ` (CPF ${respInfo.cpfMascarado})` : ''}.\n` +
          (cand.length > 1
            ? 'Há mais de um cliente com esse nome na sua base — preencha CPF e RG do responsável manualmente.'
            : 'Ele não foi encontrado na sua base de clientes. CPF completo e RG não são públicos — preencha manualmente.'));
      }
      if (!d.inscricaoEstadual) {
        alert('Dados da empresa carregados, mas o CNPJ.ws não retornou Inscrição Estadual (pode ser isento/MEI ou base defasada). Preencha manualmente — ela é obrigatória na CPFL.');
      }
      return { dados: d };
    } catch (err) {
      console.error(err);
      alert('Erro ao consultar os dados do CNPJ. Preencha manualmente.');
      return false;
    }
  };

  const handleBuscarPJ = async () => {
    const cnpj = (form.cnpj || '').replace(/\D/g, '');
    if (cnpj.length !== 14) {
      alert('CNPJ inválido. Informe um CNPJ com 14 dígitos.');
      return;
    }
    setBuscarLoading(true);
    const cnpjOk = await buscarDadosCnpjWs(cnpj);
    setBuscarLoading(false);
    if (!form.uc.trim() || !form.pn.trim()) {
      alert(cnpjOk
        ? 'Dados da empresa carregados pelo CNPJ. Informe UC e PN e clique em Buscar novamente para confirmar na CPFL.'
        : 'Para Pessoa Jurídica, informe a UC e o PN e depois clique em Buscar.');
      return;
    }
    setBuscarLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch('/api/cpfl/validar-consulta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ instalacao: form.uc.trim(), pn: form.pn.trim(), pf_pj: 2, cpf: '', cnpj })
      });
      if (resp.status === 401) {
        alert('Sessão expirada. Faça login novamente para consultar a CPFL.');
        return;
      }
      const data = await resp.json();
      if (!data.sucesso) {
        alert(`A CPFL não confirmou os dados: ${data.mensagem || 'erro desconhecido'}. Confira UC, PN e CNPJ, ou preencha manualmente.`);
        return;
      }
      const x = data.dados || {};
      const fill = (k, v) => { if (v) set(k, v); };
      fill('nome', x.nome);
      fill('uf', x.uf);
      fill('cidade', x.cidade);
      fill('bairro', x.bairro);
      fill('logradouro', x.logradouro);
      fill('numero', x.numero);
      fill('complemento', x.complemento);
      fill('cep', x.cep);

      // ValidarConsulta não fornece NovaUC. Buscamos pelo CPF do responsável
      // e associamos a NovaUC à UC antiga que foi informada no formulário.
      const candidatos = Array.isArray(cnpjOk?.dados?.candidatos)
        ? cnpjOk.dados.candidatos
        : [];
      const cpfParaConsulta = String(
        form.cpfResponsavel || (candidatos.length === 1 ? candidatos[0].cpf : '')
      ).replace(/\D/g, '');

      let novaUcEncontrada = false;
      const novaUcDireta = x.NovaUC || x.novaUC;
      if (novaUcDireta) {
        fill('instalacaoNova', String(novaUcDireta).trim());
        novaUcEncontrada = true;
      } else if (cpfParaConsulta.length === 11) {
        try {
          const params = new URLSearchParams({
            cpf: cpfParaConsulta,
            buscarPN: 'true',
            buscarNome: 'false',
          });
          const novaResp = await fetch(
            `/api/cpfl/consultar-cpf?${params.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (novaResp.status === 401) {
            alert('Sessão expirada. Faça login novamente.');
            return;
          }

          const novaData = await novaResp.json();
          const normalizarUc = (valor) =>
            String(valor || '').replace(/\D/g, '').replace(/^0+/, '');
          const ucInformada = normalizarUc(form.uc);

          const instalacaoEncontrada = (novaData.enderecos || []).find(
            (item) => normalizarUc(item.Uc) === ucInformada
          );
          const novaUc = instalacaoEncontrada?.NovaUC;

          if (novaData.sucesso && novaUc && String(novaUc).trim()) {
            fill('instalacaoNova', String(novaUc).trim());
            novaUcEncontrada = true;
          }
        } catch (err) {
          console.warn('Não foi possível consultar NovaUC pelo CPF responsável:', err);
        }
      }

      if (novaUcEncontrada) {
        alert('Dados carregados! Instalação Nova localizada.');
      } else if (cpfParaConsulta.length !== 11) {
        alert('Dados carregados. Para buscar a Instalação Nova pela CPFL, informe o CPF do responsável associado à instalação.');
      } else {
        alert('Dados carregados, mas a consulta por CPF não encontrou NovaUC para esta UC. Confira o CPF responsável ou preencha Instalação Nova manualmente.');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao buscar dados da empresa na CPFL.');
    } finally {
      setBuscarLoading(false);
    }
  };

  const handleBuscar = async () => {
    if (form.tipoPessoa === 'juridica') { return handleBuscarPJ(); }
    const cpf = form.cpf.trim().replace(/\D/g, '');
    if (!cpf || cpf.length !== 11) {
      alert('CPF inválido. Informe um CPF com 11 dígitos.');
      return;
    }
    setBuscarLoading(true);
    try {
      const token = localStorage.getItem('token');
      const ucResponse = await fetch(`/api/cpfl/consultar-cpf?cpf=${cpf}&buscarPN=true&buscarNome=true`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (ucResponse.status === 401) {
        alert('Sessão expirada. Faça login novamente para consultar a CPFL.');
        return;
      }

      const ucData = await ucResponse.json();
      if (!ucData.sucesso || ucData.total === 0) {
        alert(ucData.mensagem
          ? `Nenhuma instalação encontrada para este CPF: ${ucData.mensagem}`
          : 'Nenhuma instalação encontrada para este CPF.');
        return;
      }
      const instalacoes = ucData.enderecos || [];
      if (instalacoes.length === 1) {
        const inst = instalacoes[0];
        if (!inst.Parceiro) {
          alert('Não foi possível obter o PN para esta instalação.');
          return;
        }
        const ok = await carregarDadosInstalacao(inst.Uc, inst.Parceiro, cpf, inst.Nome, inst);
        if (ok) alert('Dados carregados!');
      } else {
        setCpfBuscado(cpf);
        setListaInstalacoes(instalacoes);
        setShowInstalacoesModal(true);
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao buscar dados.');
    } finally {
      setBuscarLoading(false);
    }
  };

  const handleSelecionarInstalacao = async (inst) => {
    setShowInstalacoesModal(false);
    setBuscarLoading(true);
    try {
      const ok = await carregarDadosInstalacao(inst.Uc, inst.Parceiro, cpfBuscado, inst.Nome, inst);
      if (ok) alert('Dados carregados!');
    } catch (err) {
      alert('Erro ao carregar dados da instalação selecionada.');
    } finally {
      setBuscarLoading(false);
    }
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    const novos = files.map(file => ({
      id: Date.now() + Math.random() * 10000,
      file,
      tipo: 'outros',
      enviar: true
    }));
    setDocs(prev => [...prev, ...novos]);
    e.target.value = '';
  };

  const updateDoc = (id, field, value) => {
    setDocs(prev => prev.map(doc => doc.id === id ? { ...doc, [field]: value } : doc));
  };

  const removeDoc = (id) => {
    setDocs(prev => prev.filter(doc => doc.id !== id));
  };
  // Busca no FTP os anexos que a CPFL já tem pra essa instalação, baixa de
  // verdade e adiciona na lista de documentos automaticamente — sem
  // precisar de nenhum clique. Fica desmarcado (enviar:false) por padrão,
  // pra escolher quais quer reenviar.
  const [buscandoAnexosFtp, setBuscandoAnexosFtp] = useState(false);
  const anexosFtpBuscadosRef = useRef(new Set());
  const buscarAnexosFtp = async () => {
    if (!form.uc || anexosFtpBuscadosRef.current.has(form.uc)) return;
    anexosFtpBuscadosRef.current.add(form.uc);
    setBuscandoAnexosFtp(true);
    try {
      const r = await apiFetch(`/api/cpfl/anexos-instalacao/${encodeURIComponent(form.uc)}`);
      const encontrados = r.arquivos || [];
      if (encontrados.length === 0) return;
      const jaTem = new Set(docs.map((d) => d.file?.name));
      const paraBuscar = encontrados.filter((arq) => !jaTem.has(arq.nome));
      const novos = [];
      for (const arq of paraBuscar) {
        try {
          const resp = await fetch(`/api/cpfl/anexo-arquivo?empresa=${encodeURIComponent(arq.empresa)}&nome=${encodeURIComponent(arq.nome)}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          const blob = await resp.blob();
          const file = new File([blob], arq.nome, { type: blob.type });
          novos.push({ id: Date.now() + Math.random() * 10000, file, tipo: 'autorizacao', enviar: false });
        } catch (err) {
          console.error(`Erro ao baixar anexo ${arq.nome}:`, err);
        }
      }
      if (novos.length > 0) setDocs((prev) => [...prev, ...novos]);
    } catch (err) {
      console.error('Erro ao buscar anexos do FTP:', err);
    } finally {
      setBuscandoAnexosFtp(false);
    }
  };
  // Dispara sozinho assim que a UC/Instalação for preenchida (por busca de
  // cliente ou digitação manual) — sem precisar clicar em nada.
  useEffect(() => {
    if (form.uc) {
      buscarAnexosFtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.uc]);

    const handleProximo = () => {
    if (form.tipoPessoa === 'juridica') {
      const d = (v) => (v || '').replace(/\D/g, '');
      if (!form.uc || !form.pn) { alert('Preencha UC e PN antes de continuar.'); return; }
      if (d(form.cnpj).length !== 14) { alert('Informe um CNPJ válido (14 dígitos).'); return; }
      if (!form.inscricaoEstadual.trim()) { alert('Preencha a Inscrição Estadual (obrigatória para PJ).'); return; }
      if ((form.dataAbertura || '').replace(/\D/g, '').length !== 8) { alert('Preencha a Data de Abertura da empresa (obrigatória para PJ).'); return; }
      if (!form.nomeResponsavel.trim()) { alert('Preencha o Nome do Responsável (obrigatório para PJ).'); return; }
      if (d(form.cpfResponsavel).length !== 11) { alert('Informe o CPF do Responsável com 11 dígitos.'); return; }
      if (!form.rgResponsavel.trim()) { alert('Preencha o RG do Responsável (obrigatório para PJ).'); return; }
      if (!form.telResidencial?.trim() && !form.telCelular?.trim()) {
        alert('Preencha ao menos um telefone (Residencial ou Celular) antes de continuar — obrigatório pela CPFL.');
        return;
      }
      const docsPJ = docs.filter(doc => doc.enviar);
      // Nos passos seguintes, cpf/rg = dados do responsável (é o que a CPFL pede no campo CPF)
      onNext({ ...form, cpf: d(form.cpfResponsavel), rg: form.rgResponsavel }, docsPJ);
      return;
    }
    if (!form.uc || !form.pn) {
      alert('Busque o cliente pelo CPF (ou preencha UC e PN manualmente) antes de continuar.');
      return;
    }
    // A CPFL exige RG, Data de Nascimento e ao menos um telefone preenchidos
    // no cadastro (CadastrarCliente) — o valor não precisa ser oficial/exato,
    // só não pode ficar vazio, senão o webservice deles quebra.
    if (!form.rg || !form.rg.trim()) {
      alert('Preencha o campo RG antes de continuar (obrigatório pela CPFL — o valor não precisa ser o oficial).');
      return;
    }
    if (!form.dataNascimento) {
      alert('Preencha a Data de Nascimento antes de continuar (obrigatório pela CPFL — o valor não precisa ser o oficial).');
      return;
    }
    if (!form.telResidencial?.trim() && !form.telCelular?.trim()) {
      alert('Preencha ao menos um telefone (Residencial ou Celular) antes de continuar — obrigatório pela CPFL.');
      return;
    }
    const docsToSend = docs.filter(doc => doc.enviar);
    onNext(form, docsToSend);
  };

  return (
    <>
      <style>{`
        .docs-list-v2 { list-style: none; margin-top: 12px; padding: 0; }
        .doc-item-v2 { background: #f9fafb; border-radius: 8px; margin-bottom: 8px; padding: 10px; border: 1px solid #e5e7eb; }
        .doc-info-v2 { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; }
        .doc-name { font-size: 14px; font-weight: 500; word-break: break-all; flex: 2; }
        .doc-controls { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .doc-tipo-select { padding: 6px 8px; border-radius: 6px; border: 1px solid #d1d5db; background: white; font-size: 13px; }
        .doc-enviar-label { display: flex; align-items: center; gap: 6px; font-size: 13px; cursor: pointer; }
        .btn-doc-remove { background: none; border: none; color: #ef4444; cursor: pointer; }

        /* Ajustes de layout da barra de busca CPFL */
        .step2-search-bar { flex-wrap: wrap; row-gap: 14px; }
        .s2-field-wide { flex: 0 0 auto; min-width: 150px; }
        .s2-field-wide input { min-width: 150px; }

        .s2-tipo-v2 { flex: 0 0 auto; min-width: 220px; }
        .s2-radio-col-v2 {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 4px;
        }
        .s2-radio-col-v2 label {
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
          font-size: 13px;
          cursor: pointer;
        }
        .s2-radio-col-v2 input[type="radio"] {
          flex: 0 0 auto;
          width: 16px;
          height: 16px;
          margin: 0;
        }
      `}</style>
      <div className="clients-view">
        {showInstalacoesModal && (
          <div className="modal-overlay" onClick={() => setShowInstalacoesModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Selecione a instalação</h3>
                <button className="modal-close" onClick={() => setShowInstalacoesModal(false)}><X size={20} /></button>
              </div>
              <div className="modal-body">
                <p>Foram encontradas {listaInstalacoes.length} instalações para o CPF {cpfBuscado.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}:</p>
                <ul className="instalacoes-list">
                  {listaInstalacoes.map((inst, idx) => (
                    <li key={idx} onClick={() => handleSelecionarInstalacao(inst)} className="instalacao-item">
                      <div><strong>UC:</strong> {inst.Uc}</div>
                      <div>{inst.Endereco || inst.Rua || ''} {inst.NumImovel || ''} - {inst.Bairro || ''}, {inst.Cidade || ''}/{inst.UF || ''}</div>
                      <div>Situação: {inst.SituacaoInstalacao || 'N/A'}</div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowInstalacoesModal(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        )}
        <div className="step2-topbar">
          <div className="step2-topbar-left">
            <div className="step-badge cpfl-badge"><Zap size={15} /> CPFL</div>
            <div>
              <h2 className="view-title" style={{ margin: 0 }}>Cadastro de Clientes — Titular CPFL</h2>
              <p className="step-description" style={{ margin: 0 }}>Dados do cliente e documentos</p>
            </div>
          </div>
          <button className="back-button" style={{ margin: 0 }} onClick={onBack}>
            <ArrowLeft size={16} /> Voltar
          </button>
        </div>
        <div className="cpfl-form-card">
          <div className="step2-search-bar">
            <button className={`titular-btn ${form.titularDoPlano ? 'active' : ''}`} onClick={() => set('titularDoPlano', !form.titularDoPlano)}>
              <span className={`titular-radio ${form.titularDoPlano ? 'checked' : ''}`} /> Titular do Plano
            </button>
            <div className="s2-field s2-field-wide"><label>UC</label><input type="text" value={form.uc} onChange={(e) => set('uc', e.target.value)} /></div>
            <div className="s2-field s2-field-wide"><label>Instalação Nova</label><input type="text" placeholder="Ex: XXX" value={form.instalacaoNova} onChange={(e) => set('instalacaoNova', e.target.value)} /></div>
            <div className="s2-field s2-field-wide"><label>PN</label><input type="text" value={form.pn} onChange={(e) => set('pn', e.target.value)} /></div>
            <div className="s2-field s2-tipo s2-tipo-v2">
              <label>TIPO</label>
              <div className="s2-radio-col-v2">
                <label><input type="radio" name="tipoPessoa" checked={form.tipoPessoa === 'fisica'} onChange={() => set('tipoPessoa', 'fisica')} /> Pessoa Física</label>
                <label><input type="radio" name="tipoPessoa" checked={form.tipoPessoa === 'juridica'} onChange={() => set('tipoPessoa', 'juridica')} /> Pessoa Jurídica</label>
              </div>
            </div>
            {form.tipoPessoa === 'juridica'
              ? <div className="s2-field s2-cpf"><label>CNPJ</label><input type="text" value={form.cnpj} onChange={(e) => set('cnpj', e.target.value)} maxLength={18} /></div>
              : <div className="s2-field s2-cpf"><label>CPF</label><input type="text" value={form.cpf} onChange={(e) => set('cpf', e.target.value)} /></div>}
            <button className="btn-buscar-cpfl" onClick={handleBuscar} disabled={buscarLoading}>
              {buscarLoading ? <div className="spinner-small" /> : <Search size={15} />} Buscar
            </button>
          </div>
          <div className="cpfl-divider" />
          <div className="cpfl-two-cols">
            <div className="cpfl-col">
              <div className="s2-field full"><label>{form.tipoPessoa === 'juridica' ? 'RAZÃO SOCIAL' : 'NOME'}</label><input type="text" value={form.nome} onChange={(e) => set('nome', e.target.value)} /></div>
              <div className="s2-field full"><label>VALOR (R$)</label><input type="text" inputMode="decimal" placeholder="0,00" value={form.valorPlano} onChange={(e) => set('valorPlano', e.target.value.replace(/[^\d.,]/g, ''))} /></div>
              {form.tipoPessoa === 'juridica' && (
                <>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div className="s2-field" style={{ flex: 1 }}><label>INSCRIÇÃO ESTADUAL</label><input type="text" value={form.inscricaoEstadual} onChange={(e) => set('inscricaoEstadual', e.target.value)} /></div>
                    <div className="s2-field" style={{ flex: 1 }}><label>DATA ABERTURA</label><input type="text" inputMode="numeric" placeholder="dd/mm/aaaa" maxLength={10} value={form.dataAbertura} onChange={(e) => set('dataAbertura', mascaraData(e.target.value))} /></div>
                  </div>
                  <div className="s2-field full"><label>NOME DO RESPONSÁVEL</label><input type="text" value={form.nomeResponsavel} onChange={(e) => set('nomeResponsavel', e.target.value)} /></div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div className="s2-field" style={{ flex: 1 }}><label>CPF RESPONSÁVEL</label><input type="text" value={form.cpfResponsavel} onChange={(e) => set('cpfResponsavel', e.target.value)} /></div>
                    <div className="s2-field" style={{ flex: 1 }}><label>RG RESPONSÁVEL</label><input type="text" value={form.rgResponsavel} onChange={(e) => set('rgResponsavel', e.target.value)} /></div>
                  </div>
                </>
              )}
              <div className="cpfl-row">
                {form.tipoPessoa !== 'juridica' && <div className="s2-field"><label>RG</label><input type="text" value={form.rg} onChange={(e) => set('rg', e.target.value)} /></div>}
                {form.tipoPessoa !== 'juridica' && <div className="s2-field"><label>DATA NASCIMENTO</label><input type="text" inputMode="numeric" placeholder="dd/mm/aaaa" maxLength={10} value={isoParaBR(form.dataNascimento) || form.dataNascimento} onChange={(e) => { const v = mascaraData(e.target.value); set('dataNascimento', brParaISO(v) || v); }} /></div>}
              </div>
              {form.tipoPessoa !== 'juridica' && <div className="s2-field full"><label>NOME CÔNJUGE</label><input type="text" value={form.nomeConjuge} onChange={(e) => set('nomeConjuge', e.target.value)} /></div>}
              <div className="cpfl-row">
                {form.tipoPessoa !== 'juridica' && <div className="s2-field"><label>CPF CÔNJUGE</label><input type="text" value={form.cpfConjuge} onChange={(e) => set('cpfConjuge', e.target.value)} /></div>}
                {form.tipoPessoa !== 'juridica' && <div className="s2-field"><label>RG CÔNJUGE</label><input type="text" value={form.rgConjuge} onChange={(e) => set('rgConjuge', e.target.value)} /></div>}
              </div>
              <div className="anexar-section">
                <input type="file" multiple ref={fileRef} style={{ display: 'none' }} onChange={handleFiles} />
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button className="btn-anexar" onClick={() => fileRef.current.click()}><CloudUpload size={17} /> Anexar Documentos</button>
                  {buscandoAnexosFtp && <span style={{ fontSize: 12, color: '#d97706' }}>Buscando anexos do cliente...</span>}
                </div>
                {docs.length > 0 && (
                  <ul className="docs-list-v2">
                    {docs.map((doc) => (
                      <li key={doc.id} className="doc-item-v2">
                        <div className="doc-info-v2">
                          <span className="doc-name">{doc.file.name}</span>
                          <div className="doc-controls">
                            <select value={doc.tipo} onChange={(e) => updateDoc(doc.id, 'tipo', e.target.value)} className="doc-tipo-select">
                              {tipoOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                            </select>
                            <label className="doc-enviar-label">
                              <input type="checkbox" checked={doc.enviar} onChange={(e) => updateDoc(doc.id, 'enviar', e.target.checked)} /> Enviar
                            </label>
                            <button className="btn-doc-remove" onClick={() => removeDoc(doc.id)}><Trash2 size={14} /></button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="cpfl-col">
              <div className="s2-field full"><label>NOME CONTATO</label><input type="text" value={form.nomeContato} onChange={(e) => set('nomeContato', e.target.value)} /></div>
              <div className="cpfl-row three">
                <div className="s2-field"><label>UF</label><input type="text" value={form.uf} onChange={(e) => set('uf', e.target.value)} maxLength={2} /></div>
                <div className="s2-field"><label>CIDADE</label><input type="text" value={form.cidade} onChange={(e) => set('cidade', e.target.value)} /></div>
                <div className="s2-field"><label>BAIRRO</label><input type="text" value={form.bairro} onChange={(e) => set('bairro', e.target.value)} /></div>
              </div>
              <div className="cpfl-row logradouro">
                <div className="s2-field grow"><label>LOGRADOURO</label><input type="text" value={form.logradouro} onChange={(e) => set('logradouro', e.target.value)} /></div>
                <div className="s2-field s2-num"><label>NÚMERO</label><input type="text" value={form.numero} onChange={(e) => set('numero', e.target.value)} /></div>
              </div>
              <div className="cpfl-row">
                <div className="s2-field s2-cep"><label>CEP</label><input type="text" value={form.cep} onChange={(e) => set('cep', e.target.value)} /></div>
                <div className="s2-field grow"><label>COMPLEMENTO</label><input type="text" value={form.complemento} onChange={(e) => set('complemento', e.target.value)} /></div>
              </div>
              <div className="cpfl-row three">
                <div className="s2-field"><label>TELEFONE RESIDENCIAL</label><input type="text" value={form.telResidencial} onChange={(e) => set('telResidencial', e.target.value)} /></div>
                <div className="s2-field"><label>TELEFONE CELULAR</label><input type="text" value={form.telCelular} onChange={(e) => set('telCelular', e.target.value)} /></div>
                <div className="s2-field"><label>TELEFONE CONTATO</label><input type="text" value={form.telContato} onChange={(e) => set('telContato', e.target.value)} /></div>
              </div>
              <div className="cpfl-row">
                <div className="s2-field grow"><label>E-MAIL</label><input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
                <div className="s2-field s2-datacad"><label>DATA CADASTRO</label><input type="text" inputMode="numeric" placeholder="dd/mm/aaaa" maxLength={10} value={isoParaBR(form.dataCadastro) || form.dataCadastro} onChange={(e) => { const v = mascaraData(e.target.value); set('dataCadastro', brParaISO(v) || v); }} /></div>
              </div>
            </div>
          </div>
          <div className="cpfl-actions">
            <button className="btn-cancel-edit" onClick={onBack}>Cancelar</button>
            <button className="btn-save-edit cpfl-save" onClick={handleProximo}>Próximo <ArrowRight size={16} /></button>
          </div>
        </div>
      </div>
    </>
  );
}

export default CpflStep2;
